import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, Minus, Plus, Trash2 } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiErrorDetail } from "@/lib/api";

const eur = (v) => `€${v.toLocaleString("en-US")}`;

export default function CartDrawer() {
  const { items, open, setOpen, updateQuantity, removeItem, subtotal, count } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const isPreorder = items.some((i) => (i.stock ?? 0) <= 0);

  const checkout = async () => {
    if (items.length === 0) return;
    if (isPreorder) {
      setOpen(false);
      navigate("/preorder");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        items: items.map((i) => ({ slug: i.slug, quantity: i.quantity })),
        origin_url: window.location.origin,
        email: user ? user.email : undefined,
      };
      const { data } = await api.post("/payments/checkout", payload);
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            className="fixed inset-0 bg-charcoal/70 backdrop-blur-sm z-[60]"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setOpen(false)}
            data-testid="cart-overlay"
          />
          <motion.aside
            className="fixed top-0 right-0 h-full w-full max-w-md bg-alabaster text-charcoal z-[70] flex flex-col"
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            data-testid="cart-drawer"
          >
            <div className="flex items-center justify-between px-7 py-6 border-b border-mineral/30">
              <span className="label-mono text-charcoal">
                Your Bag · {count} {count === 1 ? "item" : "items"}
              </span>
              <button onClick={() => setOpen(false)} data-testid="cart-close" aria-label="Close cart">
                <X size={20} />
              </button>
            </div>

            {items.length === 0 ? (
              <div className="flex-1 flex flex-col items-center justify-center gap-6 px-8 text-center">
                <p className="font-serif text-2xl font-light">Your bag is empty.</p>
                <Link
                  to="/collection"
                  onClick={() => setOpen(false)}
                  data-testid="cart-empty-cta"
                  className="btn-relic border-charcoal text-charcoal hover:bg-charcoal hover:text-alabaster"
                >
                  Explore the Collection
                </Link>
              </div>
            ) : (
              <>
                <div className="flex-1 overflow-y-auto px-7 py-6 space-y-6" data-lenis-prevent>
                  {items.map((item) => (
                    <div
                      key={item.slug}
                      className="flex gap-4 pb-6 border-b border-mineral/20"
                      data-testid={`cart-item-${item.slug}`}
                    >
                      <img
                        src={item.image || "/brand/monogram.jpg"}
                        alt={item.name}
                        className="w-20 h-24 object-cover bg-mineral/20"
                      />
                      <div className="flex-1">
                        <p className="font-serif text-lg leading-tight">{item.name}</p>
                        <p className="label-mono text-mineral mt-1">{item.volume}</p>
                        <div className="flex items-center justify-between mt-4">
                          <div className="flex items-center border border-mineral/40">
                            <button
                              onClick={() => updateQuantity(item.slug, item.quantity - 1)}
                              className="px-2 py-1.5 hover:text-amethyst"
                              data-testid={`cart-decrease-${item.slug}`}
                              aria-label="Decrease"
                            >
                              <Minus size={13} />
                            </button>
                            <span className="px-3 font-mono text-xs" data-testid={`cart-qty-${item.slug}`}>
                              {item.quantity}
                            </span>
                            <button
                              onClick={() => updateQuantity(item.slug, item.quantity + 1)}
                              className="px-2 py-1.5 hover:text-amethyst"
                              data-testid={`cart-increase-${item.slug}`}
                              aria-label="Increase"
                            >
                              <Plus size={13} />
                            </button>
                          </div>
                          <span className="font-mono text-sm">{eur(item.price * item.quantity)}</span>
                        </div>
                      </div>
                      <button
                        onClick={() => removeItem(item.slug)}
                        className="text-mineral hover:text-destructive self-start"
                        data-testid={`cart-remove-${item.slug}`}
                        aria-label="Remove"
                      >
                        <Trash2 size={15} />
                      </button>
                    </div>
                  ))}
                </div>
                <div className="px-7 py-6 border-t border-mineral/30 space-y-5">
                  <div className="flex justify-between items-baseline">
                    <span className="label-mono">Subtotal</span>
                    <span className="font-serif text-2xl" data-testid="cart-subtotal">
                      {eur(subtotal)}
                    </span>
                  </div>
                  <p className="label-mono text-mineral">
                    {isPreorder
                      ? "These fragrances are sold out — you are placing a pre-order"
                      : "Taxes & shipping calculated at checkout · Shipped from the Netherlands"}
                  </p>
                  <button
                    onClick={checkout}
                    disabled={loading}
                    data-testid="checkout-btn"
                    className="btn-relic w-full border-charcoal bg-amethyst text-ink hover:bg-ivory hover:border-ivory disabled:opacity-60"
                  >
                    {loading ? "Opening Checkout…" : isPreorder ? "Pre-Order Now" : "Proceed to Checkout"}
                  </button>
                </div>
              </>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}
